import {
  Check,
  DifferenceOutlined,
  ManageSearchOutlined,
  TurnRightOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Permissions, useScopeChecker } from 'src/rbac';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PauseIcon from '@mui/icons-material/Pause';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  Skeleton,
  TablePagination,
  TableSortLabel,
  Typography,
} from '@mui/material';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Edit02Icon from '@untitled-ui/icons-react/build/esm/Edit02';
import { formatDateOfService } from './format-date-of-service';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import {
  complianceOnBehalf,
  dorEditCodesTreatment,
  dsnDeletePermissionTreatment,
} from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { paths } from 'src/paths';
import {
  getAllCompletedDocAction,
  getTherapistInfoAction,
  resetTherapistInfo,
  undoDocumentSignAction,
} from 'src/redux/actions/documentationAction';
import {
  deleteDailySessionNoteAction,
  resetDeleteDailySessionNote,
} from 'src/redux/actions/dailySessionNotes';
import { SignatureAction, canDo } from 'src/types/signatureContext';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import {
  COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE,
  getComplianceDocTarget,
  isComplianceUnsignMode,
} from './compliance-document-on-behalf';
import OnBehalfReasonModal from './on-behalf-reason-modal';
import DeleteNoteConfirmation from '../dailySessionNotes/delete-note-confirmation';
import {
  applyArchivedReadOnly,
  ArchivedBadge,
  archivedTitleSx,
  useArchiveNote,
} from './archive-note';
import UndoSignConformation from '../dailySessionNotes/undo-Sign-conformation';
import { Scrollbar } from '../scrollbar';
import { HeldFromBillingBadge, useHoldFromBilling } from './hold-from-billing';
import { toRevisionAuditPreview, useRevisionAudit } from './revision-audit';
import PopoverMenu from './PopoverMenu';
import { holdRowTestId, noteKey } from './test-ids';

interface CompletedDocumentProps {
  activeTab: string;
  completedDocuments: Array<string>;
  documentationDoc: any;
  setDocumentationDoc: any;
  openPrintMode: any;
  setIsFilterApply: any;
  abortController: any;
}

const columns: any = [
  { id: 'documentType', label: 'Document Type' },
  { id: 'dateOfService', label: 'Date of Service' },
  { id: 'therapistSignature', label: 'Therapist Signature' },
  { id: 'supervisiorSignature', label: 'Supervisior Signature' },
  { id: 'physicianSignature', label: 'Physician Signature' },
  { id: 'action', label: '', align: 'right' },
];

const notSortingHeader = [
  'therapistSignature',
  'supervisiorSignature',
  'physicianSignature',
  'action',
];

//  BOPS-151: the one action an archived note keeps. Used both as the item's label and as the
//  survivor passed to applyArchivedReadOnly, so the two cannot drift apart.
const VIEW_DOCUMENT_LABEL = 'View Document';

const CompletedDocument: FC<CompletedDocumentProps> = (props: CompletedDocumentProps) => {
  const {
    activeTab,
    completedDocuments,
    documentationDoc,
    setDocumentationDoc,
    openPrintMode,
    setIsFilterApply,
    abortController,
  } = props;
  const { state } = useLocation();
  const dispatch = useDispatch();
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const episodeOfCareId = state?.episodeOfCareId;

  const [dorEditCodesEnabled] = useFeatureToggleWhenReady(dorEditCodesTreatment, 'FACILITY');

  //  THIN-7799: the Delete row needs the treatment ON as well as the permission.
  //  Fails closed — the hook returns null until the Split SDK is ready.
  const [isDsnDeleteEnabled] = useFeatureToggleWhenReady(
    dsnDeletePermissionTreatment,
    'ENTERPRISE'
  );

  const [providerId] = useCurrentUser(['providerId', 'discipline']);
  const getAllCompletedDoc = useSelector((state: any) => state.getAllCompetedDocReducer);
  const holdFromBilling = useHoldFromBilling();

  //pagination logic

  const [rowPerPage, setRowPerPage] = useState(30);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('dateOfService');
  const [sortOrderArray, setSortArray] = useState<any>(['desc', 'desc', 'desc', 'desc', 'desc']);
  // Sticky Select All at the table-header level. Independent of the parent's
  // top-level "Select All" button so that even if the user only interacts
  // with this table's checkbox, records that arrive on later pages get
  // auto-selected too.
  const [isSelectAllCompletedDoc, setIsSelectAllCompletedDoc] = useState(false);

  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  const facilityName =
    getAssignedFacilities?.response?.data?.data?.find(
      (fac: any) => fac?.uuid === localStorage.getItem('facilityId')
    )?.name ?? '';

  const onPageChange = (_event: any, newPage: number) => {
    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: newPage,
          pageSize: rowPerPage,
        },
      })
    );
    setPage(newPage);
  };

  const getSortOrder = (sortColumn: string) => {
    switch (sortColumn) {
      case 'documentType':
        return sortOrderArray[0];
      case 'dateOfService':
        return sortOrderArray[1];
      default:
        return '';
    }
  };

  const getSortOrder1 = (sortColumn: string, newSortOrderArray: any) => {
    switch (sortColumn) {
      case 'documentType':
        return newSortOrderArray[0];
      case 'dateOfService':
        return newSortOrderArray[1];
      default:
        return '';
    }
  };

  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);
    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: 0,
          pageSize: newRowsPerPage,
        },
      })
    );
    setPage(0);
  };
  const createSortHandler = (columnId: string, index: number) => {
    const isAsc = sortOrderArray[index] === 'asc';
    const newSortOrderArray = ['desc', 'desc', 'desc', 'desc', 'desc'];
    newSortOrderArray[index] = isAsc ? 'desc' : 'asc';

    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder1(columnId, newSortOrderArray),
          sortBy: columnId,
          page: page,
          pageSize: rowPerPage,
        },
      })
    );
    setSortArray(newSortOrderArray);
    setSortBy(columnId);
  };

  useEffect(() => {
    if (!activeTab) return;
    setSortArray(['desc', 'desc', 'desc', 'desc', 'desc']);
    setSortBy('dateOfService');
    setPage(0);
    setRowPerPage(30);
  }, [activeTab]);

  const { patients, patientDetails, episodeOfCare, details, previewDocument } = paths;
  const navigate = useNavigate();
  const has = useScopeChecker();

  const setPreviewDocument = (document: any) => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${previewDocument}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        signedDocuments: document?.showAuditTrail === true,
      },
    });
  };

  const [, setRemoveSign] = useState('');
  const [documentPendingUnsign, setDocumentPendingUnsign] = useState<any>(null);
  //  BRK-2073: row pending the on-behalf reason modal (compliance unsign fork).
  const [unsignOnBehalfDoc, setUnsignOnBehalfDoc] = useState<any>(null);
  const [isComplianceOnBehalf] = useFeatureToggleWhenReady(complianceOnBehalf, 'ENTERPRISE');

  const [deleteDocument, setDeleteDocument] = useState<any>(null);
  const deleteDsnState = useSelector((state: any) => state?.deleteDailySessionNoteReducer);

  const handleConfirmDeleteNote = (reason: string) => {
    dispatch(
      deleteDailySessionNoteAction({ dsnUuid: deleteDocument?.dailySessionNoteUuid, reason })
    );
  };

  //  Re-read the list on the current page with the filters and sort the user is actually
  //  looking at. Deliberately not memoised: it is only ever called from an effect that reruns
  //  when its trigger flips, so a plain function keeps page/sort/rowPerPage current instead of
  //  capturing whatever they were when a useCallback was last rebuilt.
  const refetchCompletedDocuments = () => {
    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: page,
          pageSize: rowPerPage,
        },
      })
    );
  };

  useEffect(() => {
    //  Only react to deletes initiated from this list — signature-pending-list shares the
    //  reducer and handles its own refresh (and the shared reset is idempotent).
    if (deleteDsnState?.isSuccess && deleteDocument) {
      setDeleteDocument(null);
      dispatch(resetDeleteDailySessionNote());
      refetchCompletedDocuments();
    }
  }, [deleteDsnState?.isSuccess]);

  //  BOPS-151: refetch the list once an archive lands, exactly as the delete above does.
  //  Archiving runs the DSN delete underneath, so the row and the "N Document" count are both
  //  stale the moment it succeeds — the reducer's in-place patch only carries the row until
  //  this response replaces it. Whatever the list returns for an archived note is the
  //  backend's answer to give; the page must not keep showing the pre-archive one.
  const archiveNote = useArchiveNote({
    episodeOfCareId,
    onArchived: refetchCompletedDocuments,
  });

  //  BRK-2036: the LOCAL refetch on purpose — it re-reads the page/sort the user is looking at,
  //  where the parent's callGetAllSubmittedDocument chain resets pagination and would jump a
  //  user back to page 1 for generating a report on a row further down.
  const revisionAudit = useRevisionAudit({ onGenerated: refetchCompletedDocuments });

  const removedSignDocument = (document: any) => {
    //  BRK-2073: compliance mode (marker + scope + the epic's single Split) swaps the plain
    //  confirmation for the mandatory reason+consent modal; everyone else is byte-identical.
    if (
      isComplianceUnsignMode({
        complianceSplitOn: isComplianceOnBehalf,
        hasDocumentScope: has(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
        document,
      })
    ) {
      //  Reset first: the identity reducer is page-global, so a lookup from an earlier action
      //  would otherwise show the WRONG identity when this row's target id is missing — the
      //  modal fails closed on a blank identity (confirm disabled) instead.
      dispatch(resetTherapistInfo());
      const target = getComplianceDocTarget(document);
      if (target?.onBehalfOfProviderId) {
        dispatch(getTherapistInfoAction({ providerId: target.onBehalfOfProviderId }));
      }
      setUnsignOnBehalfDoc(document);
      return;
    }
    setDocumentPendingUnsign(document);
  };

  const confirmUnsignOnBehalf = (document: any, reason: string) => {
    setRemoveSign(document.uuid);
    //  Same FSM-aware endpoint as the legacy confirm; onBehalfReason is the BRK-2072
    //  discriminator that makes the backend verify the grant and stamp provenance.
    dispatch(
      undoDocumentSignAction({
        formSubmissionUuid: document.uuid,
        documentType: document?.documentType?.replaceAll(' ', '_')?.toUpperCase(),
        onBehalfReason: reason,
      })
    );
    setUnsignOnBehalfDoc(null);
  };

  const confirmUnsignDocument = () => {
    if (!documentPendingUnsign) return;
    const document = documentPendingUnsign;
    setRemoveSign(document.uuid);
    // THIN-7258: always route through the FSM-aware unsign endpoint. The
    // legacy /provider/document/edit-signed-document is a deprecated
    // no-op (DocumentationServiceImpl.editSignedDocument). The unified
    // FSM (THIN-7164) handles both reform-signed and legacy Jotform-
    // signed docs.
    dispatch(
      undoDocumentSignAction({
        formSubmissionUuid: document.uuid,
        documentType: document?.documentType?.replaceAll(' ', '_')?.toUpperCase(),
      })
    );
    setDocumentPendingUnsign(null);
  };

  const checkUndoPermission = (document: any): boolean => {
    // signatureContext-driven: BE reports CAN_UNSIGN on whichever axis the current user
    // owns (therapist on the doc they authored, supervisor on a doc they cosigned,
    // physician on a locked doc, or any privileged unsigner). Mirrors the check in
    // signature-pending-list.tsx. The legacy author-only gate (providerId === current
    // user) hid Undo Signature for supervisors/physicians since they aren't the author.
    const ctx = document?.signatureContext;
    if (!ctx) return false;
    return (
      canDo(ctx.allowedTherapistSignatureActions, SignatureAction.CAN_UNSIGN) ||
      canDo(ctx.allowedSupervisorSignatureActions, SignatureAction.CAN_UNSIGN) ||
      canDo(ctx.allowedPhysicianSignatureActions, SignatureAction.CAN_UNSIGN)
    );
  };

  const checkReadPermission = (document: any) => {
    if (document.formType?.includes('RECERTIFICATION') && !has(Permissions.READ_RECERTIFICATION)) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.READ_EVAL)) {
      return false;
    }
    return true;
  };

  const onSelectAllClickCompletedDoc = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFilterApply(false);
    if (event.target.checked) {
      setIsSelectAllCompletedDoc(true);
      // Union with existing so items already added from other pages stay.
      setDocumentationDoc((prev: any) => {
        const existing = Array.isArray(prev?.completedDoc) ? prev.completedDoc : [];
        const seen = new Set(existing.map((d: any) => d?.uuid));
        const extras = (completedDocuments || []).filter((d: any) => d?.uuid && !seen.has(d.uuid));
        return { ...prev, completedDoc: extras.length ? [...existing, ...extras] : existing };
      });
      // Fetch every remaining record so the sync effect can add them to the
      // selection without the user having to click through each page.
      const totalElements = getAllCompletedDoc?.response?.totalElements;
      const fetchAllSize =
        totalElements && totalElements > (completedDocuments?.length ?? 0) ? totalElements : null;
      if (fetchAllSize) {
        setRowPerPage(fetchAllSize);
        setPage(0);
        dispatch(
          getAllCompletedDocAction({
            therapistUuid: providerId,
            eocId: state?.episodeOfCareId,
            signal: abortController,
            params: {
              type: activeTab,
              filter: 'COMPLETED',
              sortDirection: getSortOrder(sortBy),
              sortBy: sortBy,
              page: 0,
              pageSize: fetchAllSize,
            },
          })
        );
      }
      return;
    }
    setIsSelectAllCompletedDoc(false);
    setDocumentationDoc((prev: any) => ({
      ...prev,
      completedDoc: [],
    }));
  };

  // Whenever the server returns a new page of rows and the header Select All
  // is still "on", union those rows into the selection by uuid so the 31st,
  // 32nd, … records get auto-checked as they come into view.
  useEffect(() => {
    if (!isSelectAllCompletedDoc) return;
    if (!completedDocuments?.length) return;
    setDocumentationDoc((prev: any) => {
      const existing = Array.isArray(prev?.completedDoc) ? prev.completedDoc : [];
      const seen = new Set(existing.map((d: any) => d?.uuid));
      const extras = (completedDocuments || []).filter((d: any) => d?.uuid && !seen.has(d.uuid));
      if (!extras.length) return prev;
      return { ...prev, completedDoc: [...existing, ...extras] };
    });
  }, [completedDocuments, isSelectAllCompletedDoc, setDocumentationDoc]);

  // Flag-independent safety net: if every previously visible row was in the
  // selection (i.e. the user had "all visible" checked, however it was
  // triggered — parent Select All, header checkbox, or manual clicks), and
  // new rows appear on subsequent pages, union them into the selection too.
  const prevCompletedDocsRef = useRef<any[]>([]);
  useEffect(() => {
    const prev = prevCompletedDocsRef.current || [];
    const nextList: any[] = Array.isArray(completedDocuments) ? completedDocuments : [];
    if (prev.length === 0 || nextList.length === 0) {
      prevCompletedDocsRef.current = nextList;
      return;
    }
    const selectedUuids = new Set((documentationDoc?.completedDoc || []).map((d: any) => d?.uuid));
    const prevAllSelected = prev.every((d: any) => d?.uuid && selectedUuids.has(d.uuid));
    if (prevAllSelected) {
      setDocumentationDoc((state: any) => {
        const existing = Array.isArray(state?.completedDoc) ? state.completedDoc : [];
        const seen = new Set(existing.map((d: any) => d?.uuid));
        const extras = nextList.filter((d: any) => d?.uuid && !seen.has(d.uuid));
        if (!extras.length) return state;
        return { ...state, completedDoc: [...existing, ...extras] };
      });
    }
    prevCompletedDocsRef.current = nextList;
  }, [completedDocuments, documentationDoc?.completedDoc, setDocumentationDoc]);
  const handleNavigateToDailySessionNote = (document: any) => {
    const selectedPatientData = {
      name: patientData?.name,
      uuid: patientData?.uuid,
      firstName: patientData?.firstName,
      lastName: patientData?.lastName,
      notesId: patientData?.dailySessionNoteUuid,
    };
    navigate('/daily-session-notes', {
      state: {
        document: {
          dateOfService: document?.dateOfService,
          source: 'FROM_EOC',
          dailySessionNoteUuid: document?.dailySessionNoteUuid,
          patientEoc: selectedPatientData,
        },
      },
    });
  };

  const checkEditMedicalCodesPermission = (document: any) => {
    if (
      document.formType?.includes('EVALUATION') &&
      has(Permissions.EDIT_MEDICAL_CODE) &&
      dorEditCodesEnabled === true
    ) {
      return true;
    }

    return false;
  };

  const handleEditMedicalCodesClick = (document: any) => {
    navigate('/edit-medical-codes', {
      state: {
        episodeOfCareId: state.episodeOfCareId,
        episodeOfCareTitle: state.episodeOfCareTitle,
        document: document,
        patient: patientData,
      },
    });
  };

  return (
    <>
      <Box
        // sx={{ border: '1px solid #F2F4F7', borderRadius: '20px' }}
        p={3}
      >
        <Card>
          <Grid
            container
            alignItems={'center'}
            p={2}
          >
            <Typography variant="h6">Completed Documents</Typography>

            <Chip
              label={`${
                getAllCompletedDoc?.response?.totalElements
                  ? getAllCompletedDoc?.response?.totalElements
                  : '0'
              } Document`}
              variant="outlined"
              sx={{ marginLeft: '10px' }}
            />
          </Grid>
          <Divider />
          <Scrollbar>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {columns?.map((column: any, index: any) => (
                    <TableCell
                      key={column.id}
                      align={column?.align}
                    >
                      {!notSortingHeader.includes(column?.id) ? (
                        <>
                          {openPrintMode && column?.id === 'documentType' ? (
                            <Checkbox
                              color="primary"
                              indeterminate={
                                documentationDoc?.completedDoc?.length > 0 &&
                                documentationDoc?.completedDoc?.length < completedDocuments?.length
                              }
                              checked={
                                completedDocuments?.length > 0 &&
                                documentationDoc?.completedDoc?.length ===
                                  completedDocuments?.length
                              }
                              onChange={onSelectAllClickCompletedDoc}
                              inputProps={{
                                'aria-label': 'select all desserts',
                              }}
                            />
                          ) : null}
                          <TableSortLabel
                            active={true}
                            direction={sortOrderArray[index]}
                            onClick={() => createSortHandler(column.id, index)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getAllCompletedDoc?.loader ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box
                        textAlign="center"
                        p={3}
                      >
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : getAllCompletedDoc?.response?.totalElements ? (
                  completedDocuments.map((document: any) => (
                    <TableRow
                      // hover
                      key={document.uuid}
                      sx={{
                        backgroundColor: documentationDoc?.completedDoc?.some(
                          (item: any) => item?.uuid === document?.uuid
                        )
                          ? '#EBEEFE'
                          : null,
                      }}
                      onClick={() => {
                        if (openPrintMode) {
                          setIsFilterApply(false);
                          const chipIndex = documentationDoc?.completedDoc?.find(
                            (chip: any) => chip?.uuid === document?.uuid
                          );
                          if (chipIndex) {
                            const updatedChips = documentationDoc?.completedDoc?.filter(
                              (chip: any) => chip?.uuid !== document?.uuid
                            );

                            setIsSelectAllCompletedDoc(false);
                            setDocumentationDoc((prev: any) => ({
                              ...prev,
                              completedDoc: updatedChips,
                            }));
                            return;
                          } else {
                            setDocumentationDoc((prev: any) => ({
                              ...prev,
                              completedDoc: [...(documentationDoc?.completedDoc ?? []), document],
                            }));
                          }
                          return;
                        }
                      }}
                    >
                      <TableCell>
                        <Grid
                          container
                          alignItems="center"
                          direction="row"
                          // spacing={1}
                        >
                          {/* <ProgressNote /> */}
                          {openPrintMode ? (
                            <Checkbox
                              checked={documentationDoc?.completedDoc?.some(
                                (item: any) => item?.uuid === document?.uuid
                              )}
                            />
                          ) : null}
                          {/*  BOPS-151: an archived note reads as struck through, matching the
                              Archived Documents table below. Gated with the badge, not on its
                              own: BOPS-173 says a user without the permission must not learn a
                              note was archived, and a struck-through title would say it. */}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              marginLeft: '4px',
                              ...(archiveNote.isArchived(document) ? archivedTitleSx : {}),
                            }}
                          >
                            {document.documentType}
                          </Typography>
                          {holdFromBilling.isHeldFromBilling(document) && (
                            <HeldFromBillingBadge origin={holdFromBilling.holdOrigin(document)} />
                          )}
                          {archiveNote.isArchived(document) && <ArchivedBadge />}
                        </Grid>
                      </TableCell>
                      <TableCell>
                        {formatDateOfService(document.dateOfService, 'dd MMM, yyyy') || '-'}
                      </TableCell>
                      {/* THIN-7286: read signer names from signatureContext per axis instead
                          of the legacy signedBy[0] / supervisorName fields. signedBy ordering was
                          not axis-aware and supervisorName was overloaded to hold the physician's
                          name on 3-axis-signed Eval/Recerts. signatureContext carries one name
                          per axis (established pattern, see signature-pending-list.tsx:549). */}
                      <TableCell>
                        <Typography variant="subtitle2">
                          {document?.signatureContext?.therapist?.name || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ cursor: 'pointer' }}>
                        <Typography variant="subtitle2">
                          {document?.signatureContext?.supervisor?.name || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ cursor: 'pointer' }}>
                        <Typography variant="subtitle2">
                          {document?.signatureContext?.physician?.name || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{ position: 'relative' }}
                      >
                        <PopoverMenu
                          testId={holdRowTestId('note', noteKey(document), 'menu')}
                          //  BOPS-151: strip the menu to View Document once the note is archived.
                          //  The signature-pending list applies the same helper to its own menu.
                          items={applyArchivedReadOnly(
                            [
                              {
                                label: VIEW_DOCUMENT_LABEL,
                                onClick: () => setPreviewDocument(document),
                                icon: <VisibilityOutlined fontSize="small" />,
                                disabled: !checkReadPermission(document),
                              },
                              {
                                //  BRK-2036: the stored revision-appendix PDF. Same read gate as
                                //  View Document above — it is the same document, plus an
                                //  appendix — so the permission check is written once, here,
                                //  rather than duplicated inside useRevisionAudit.
                                label: 'View Document With Revisions',
                                onClick: () => setPreviewDocument(toRevisionAuditPreview(document)),
                                icon: <ManageSearchOutlined fontSize="small" />,
                                disabled:
                                  !revisionAudit.canView(document) ||
                                  !checkReadPermission(document),
                              },
                              {
                                //  BRK-2036: on-demand — nothing is generated until asked for.
                                //  Shown alongside View when the stored export is stale, so the
                                //  old one stays readable while a current one is produced.
                                label: 'Generate Document With Revisions',
                                onClick: () => revisionAudit.requestGenerate(document),
                                icon: <DifferenceOutlined fontSize="small" />,
                                disabled: !revisionAudit.canGenerate(document),
                              },
                              {
                                label: 'Edit ICD-10 Codes',
                                onClick: () => handleEditMedicalCodesClick(document),
                                icon: <Edit02Icon fontSize="small" />,
                                disabled: !checkEditMedicalCodesPermission(document),
                              },
                              {
                                label: 'Go to Session Notes',
                                onClick: () => handleNavigateToDailySessionNote(document),
                                icon: <TurnRightOutlined fontSize="small" />,
                                disabled:
                                  !document?.formType?.includes(
                                    'DAILY_SOAP_NOTE',
                                    'DAILY_SOAP_NOTE_REFORM'
                                  ) || document?.providerId !== providerId,
                              },
                              {
                                label: 'Undo Signature',
                                onClick: () => removedSignDocument(document),
                                icon: <RestoreOutlinedIcon fontSize="small" />,
                                disabled: !checkUndoPermission(document),
                              },
                              {
                                label: 'Hold from billing',
                                testId: holdRowTestId('note', noteKey(document), 'hold'),
                                onClick: () => holdFromBilling.requestHold(document),
                                icon: (
                                  <PauseIcon
                                    fontSize="small"
                                    sx={{ color: 'warning.dark' }}
                                  />
                                ),
                                sx: { color: 'warning.dark' },
                                //  THIN-8138: offered even inside a closed period, then refused
                                //  with an explanation — see `isHoldBlockedByClosedPeriod`.
                                disabled: !(
                                  holdFromBilling.canHold(document) ||
                                  holdFromBilling.isHoldBlockedByClosedPeriod(document)
                                ),
                              },
                              {
                                label: 'Include in billing',
                                testId: holdRowTestId('note', noteKey(document), 'include'),
                                onClick: () => holdFromBilling.includeInBilling(document),
                                icon: (
                                  <Check
                                    fontSize="small"
                                    sx={{ color: 'success.main' }}
                                  />
                                ),
                                //  THIN-8138: offered even inside a closed period, then refused
                                //  with an explanation — see `isIncludeBlockedByClosedPeriod`.
                                disabled: !(
                                  holdFromBilling.canInclude(document) ||
                                  holdFromBilling.isIncludeBlockedByClosedPeriod(document)
                                ),
                              },
                              {
                                //  Archive button gated on archiveButton from BE and useArchiveNote rules.
                                label: 'Archive',
                                onClick: () => archiveNote.requestArchive(document),
                                icon: (
                                  <ArchiveOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: 'error.main' }}
                                  />
                                ),
                                sx: { color: 'error.main' },
                                disabled: false,
                                hidden: !archiveNote.canArchive(document),
                                dividerBefore: true,
                              },
                              {
                                label: 'Delete',
                                onClick: () => setDeleteDocument(document),
                                dividerBefore: true,
                                icon: (
                                  <DeleteIcon
                                    fontSize="small"
                                    sx={{ color: 'error.main' }}
                                  />
                                ),
                                sx: { color: 'error.main' },
                                //  THIN-7825: fully signed DSNs land in this Completed list, and the
                                //  eligibility rule (THIN-7634) says co-sign does NOT block deletion —
                                //  only a closed month does, via the BE deletable flag. Same gating as
                                //  signature-pending-list.
                                //  BOPS-446: no permission check here — `deletable` is the server's
                                //  answer for this user (Any key, Own key on notes they wrote, closed
                                //  month), so an own-only user sees Delete on their own notes. Only an
                                //  explicit true shows it: with no permission check behind it, an absent
                                //  flag would offer a hard delete to everyone.
                                disabled:
                                  !isDsnDeleteEnabled ||
                                  document?.formType !== 'DAILY_SOAP_NOTE_REFORM' ||
                                  document?.deletable !== true,
                              },
                            ].filter((item) => !item.disabled),
                            archiveNote.isReadOnly(document),
                            VIEW_DOCUMENT_LABEL
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box
                        textAlign="center"
                        p={3}
                      >
                        <Typography
                          color={'text.secondary'}
                          variant="subtitle2"
                        >
                          No Records Found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Scrollbar>
          <TablePagination
            component="div"
            count={getAllCompletedDoc?.response?.totalElements || 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={page}
            rowsPerPage={rowPerPage}
            rowsPerPageOptions={[30, 50, 100]}
          />
        </Card>
      </Box>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setDocumentPendingUnsign(null)}
        open={Boolean(documentPendingUnsign)}
      >
        <UndoSignConformation
          close={() => setDocumentPendingUnsign(null)}
          confirm={confirmUnsignDocument}
          title="Undo Signature"
          message="Are you sure you want to unsign this document? You will need to re-sign it if you confirm."
        />
      </BootstrapDialog>

      {/* BRK-2073: unsign on behalf — mandatory reason + consents */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setUnsignOnBehalfDoc(null)}
        open={Boolean(unsignOnBehalfDoc)}
      >
        {unsignOnBehalfDoc && (
          <OnBehalfReasonModal
            document={unsignOnBehalfDoc}
            onClose={() => setUnsignOnBehalfDoc(null)}
            onConfirm={confirmUnsignOnBehalf}
            title="Unsigning on behalf of"
            actionPhrase="unsigning this document"
            onBehalfOfLabel={
              getComplianceDocTarget(unsignOnBehalfDoc)?.axis === 'SUPERVISOR'
                ? 'Supervisor'
                : 'Therapist'
            }
            confirmLabel="Unsign"
          />
        )}
      </BootstrapDialog>
      {/* Delete DSN confirmation */}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '520px' } }}
        onClose={() => setDeleteDocument(null)}
        aria-labelledby="delete-note-dialog-title"
        open={Boolean(deleteDocument)}
      >
        <DeleteNoteConfirmation
          close={() => setDeleteDocument(null)}
          confirm={handleConfirmDeleteNote}
          patientName={`${patientData?.lastName ?? ''} ${patientData?.firstName ?? ''}`.trim()}
          dateOfService={formatDateOfService(deleteDocument?.dateOfService, 'MMMM d, yyyy')}
          loading={deleteDsnState?.loading}
          therapistName={
            deleteDocument?.signatureContext?.therapist?.name
              ? deleteDocument?.signatureContext?.therapist?.name
              : ''
          }
          facility={facilityName}
        />
      </BootstrapDialog>
      {holdFromBilling.holdConfirmationDialog}
      {holdFromBilling.closedPeriodDialog}
      {archiveNote.archiveConfirmationDialog}
    </>
  );
};

export default CompletedDocument;
